import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  Search,
  RefreshCw,
  Shield,
  Clock,
  AlertCircle,
  X,
  Globe,
  Key,
} from 'lucide-react';
import {
  searchBackends,
  SearchBackend,
  CreateSearchBackendRequest,
  UpdateSearchBackendRequest,
} from '../api/client';
import { formatDateTime, formatNumber, cn } from '../lib/utils';

export default function SearchBackends() {
  const [backends, setBackends] = useState<SearchBackend[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBackend, setEditingBackend] = useState<SearchBackend | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const res = await searchBackends.list();
      setBackends(res.backends);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch backends';
      console.error('Failed to fetch search backends:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, []);

  const handleCreateBackend = async (data: CreateSearchBackendRequest) => {
    try {
      setError(null);
      await searchBackends.create(data);
      setShowCreateModal(false);
      await fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create backend';
      console.error('Failed to create search backend:', err);
      setError(message);
    }
  };

  const handleUpdateBackend = async (id: string, data: UpdateSearchBackendRequest) => {
    try {
      setError(null);
      await searchBackends.update(id, data);
      setEditingBackend(null);
      await fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update backend';
      console.error('Failed to update search backend:', err);
      setError(message);
    }
  };

  const handleToggleActive = async (backend: SearchBackend) => {
    try {
      await searchBackends.update(backend.id, { isActive: !backend.isActive });
      await fetchData();
    } catch (err) {
      console.error('Failed to toggle search backend:', err);
    }
  };

  const handleDeleteBackend = async (backend: SearchBackend) => {
    if (!confirm(`Delete search backend "${backend.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await searchBackends.delete(backend.id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete search backend:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading backends...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Search Backends</h1>
          <p className="text-muted-foreground mt-1">
            Manage web search providers (Tavily, etc.) used by the assistant's web_search tool
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-glow-violet px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Backend
          </span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Empty state hint — surfaces the "toggle to disable = kill switch" behavior */}
      {backends.length > 0 && !backends.some(b => b.isActive) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-300/80">
          No backend is currently active. Web search requests from iOS will return 503 "search unavailable".
          Toggle a backend to <span className="font-semibold">Enabled</span> to resume search.
        </div>
      )}

      {/* Backends Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {backends.map((backend, index) => (
          <BackendCard
            key={backend.id}
            backend={backend}
            delay={index * 100}
            onEdit={() => setEditingBackend(backend)}
            onToggle={() => handleToggleActive(backend)}
            onDelete={() => handleDeleteBackend(backend)}
          />
        ))}
        {backends.length === 0 && (
          <div className="col-span-2 card-glow rounded-2xl p-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">No search backends configured</p>
                <p className="text-sm text-muted-foreground/70">
                  Add a Tavily backend to enable the web_search tool in the iOS app
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-secondary px-4 py-2 rounded-lg text-sm"
              >
                Add your first backend
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <BackendModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => handleCreateBackend(data as CreateSearchBackendRequest)}
        />
      )}

      {/* Edit Modal */}
      {editingBackend && (
        <BackendModal
          existingBackend={editingBackend}
          onClose={() => setEditingBackend(null)}
          onSubmit={(data) => handleUpdateBackend(editingBackend.id, data)}
        />
      )}
    </div>
  );
}

function BackendCard({
  backend,
  delay,
  onEdit,
  onToggle,
  onDelete,
}: {
  backend: SearchBackend;
  delay: number;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const statusStyles: Record<string, { bg: string; text: string; badge: string }> = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400' },
    gray:    { bg: 'bg-gray-500/10',    text: 'text-gray-400',    badge: 'bg-gray-500/15 text-gray-400' },
  };
  const statusKey = backend.isActive ? 'emerald' : 'gray';
  const statusText = backend.isActive ? 'Active' : 'Inactive';
  const styles = statusStyles[statusKey];

  const stats = backend.stats;
  const successRate = stats && stats.totalRequests > 0
    ? ((stats.successfulRequests / stats.totalRequests) * 100).toFixed(1)
    : '0';

  return (
    <div
      className="card-glow rounded-2xl p-6 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', styles.bg)}>
            <Search className={cn('w-6 h-6', styles.text)} />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{backend.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={cn('px-2 py-0.5 text-xs font-medium rounded-md capitalize', styles.badge)}>
                {statusText}
              </span>
              <span className="text-xs text-muted-foreground capitalize">{backend.type}</span>
              <span className="text-xs text-muted-foreground capitalize">{backend.searchDepth}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-cyan-400 transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* URL + key indicator */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-secondary/50">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <code className="text-sm font-mono text-muted-foreground truncate flex-1" title={backend.baseUrl}>
          {backend.baseUrl}
        </code>
        {backend.hasApiKey ? (
          <div className="flex items-center gap-1 text-xs text-amber-400">
            <Key className="w-3 h-3" />
            <span>API Key</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-rose-400">
            <Key className="w-3 h-3" />
            <span>No Key</span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">24h Requests</p>
          <p className="font-mono font-medium">{formatNumber(stats?.totalRequests ?? 0)}</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Success</p>
          <p className="font-mono font-medium text-emerald-400">{successRate}%</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Avg Time</p>
          <p className="font-mono font-medium">
            {stats ? (stats.avgResponseTime / 1000).toFixed(1) : '0.0'}s
          </p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Priority</p>
          <p className="font-mono font-medium">{backend.priority}</p>
        </div>
      </div>

      {/* Additional config row */}
      <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-muted-foreground">Max Results</p>
          <p className="font-mono font-medium">{backend.maxResultsDefault}</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-muted-foreground">Cost / query</p>
          <p className="font-mono font-medium">
            {backend.costPerQueryUsd != null ? `$${backend.costPerQueryUsd.toFixed(4)}` : '—'}
          </p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-muted-foreground">Timeout</p>
          <p className="font-mono font-medium">{(backend.timeoutMs / 1000).toFixed(1)}s</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>Updated {formatDateTime(backend.updatedAt)}</span>
        </div>
        <button
          onClick={onToggle}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
            backend.isActive
              ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          )}
        >
          {backend.isActive ? 'Enabled' : 'Disabled'}
        </button>
      </div>
    </div>
  );
}

function BackendModal({
  existingBackend,
  onClose,
  onSubmit,
}: {
  existingBackend?: SearchBackend;
  onClose: () => void;
  onSubmit: (data: CreateSearchBackendRequest | UpdateSearchBackendRequest) => void;
}) {
  const isEditing = !!existingBackend;

  const [name,       setName]       = useState(existingBackend?.name       || '');
  const [type]                     = useState<'tavily'>(existingBackend?.type || 'tavily');
  const [baseUrl,    setBaseUrl]    = useState(existingBackend?.baseUrl    || 'https://api.tavily.com');
  const [apiKey,     setApiKey]     = useState('');
  const [searchDepth, setSearchDepth] = useState<'basic' | 'advanced'>(existingBackend?.searchDepth || 'basic');
  const [maxResultsDefault, setMaxResultsDefault] = useState(
    (existingBackend?.maxResultsDefault ?? 5).toString()
  );
  const [costPerQueryUsd, setCostPerQueryUsd] = useState(
    existingBackend?.costPerQueryUsd != null ? existingBackend.costPerQueryUsd.toString() : ''
  );
  const [priority,   setPriority]   = useState((existingBackend?.priority ?? 100).toString());
  const [timeoutMs,  setTimeoutMs]  = useState((existingBackend?.timeoutMs ?? 30000).toString());
  const [maxConcurrent, setMaxConcurrent] = useState((existingBackend?.maxConcurrent ?? 10).toString());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsedCost = costPerQueryUsd.trim() === '' ? null : Number(costPerQueryUsd);

    if (isEditing) {
      const data: UpdateSearchBackendRequest = {};
      if (name !== existingBackend.name) data.name = name;
      if (baseUrl !== existingBackend.baseUrl) data.baseUrl = baseUrl;
      if (apiKey) data.apiKey = apiKey;
      if (searchDepth !== existingBackend.searchDepth) data.searchDepth = searchDepth;
      if (parseInt(maxResultsDefault, 10) !== existingBackend.maxResultsDefault) {
        data.maxResultsDefault = parseInt(maxResultsDefault, 10);
      }
      if (parsedCost !== existingBackend.costPerQueryUsd) data.costPerQueryUsd = parsedCost;
      if (parseInt(priority, 10) !== existingBackend.priority) data.priority = parseInt(priority, 10);
      if (parseInt(timeoutMs, 10) !== existingBackend.timeoutMs) data.timeoutMs = parseInt(timeoutMs, 10);
      if (parseInt(maxConcurrent, 10) !== existingBackend.maxConcurrent) {
        data.maxConcurrent = parseInt(maxConcurrent, 10);
      }
      onSubmit(data);
    } else {
      const data: CreateSearchBackendRequest = {
        name,
        type,
        baseUrl,
        searchDepth,
        maxResultsDefault: parseInt(maxResultsDefault, 10),
        costPerQueryUsd: parsedCost,
        priority: parseInt(priority, 10),
        timeoutMs: parseInt(timeoutMs, 10),
        maxConcurrent: parseInt(maxConcurrent, 10),
      };
      if (apiKey) data.apiKey = apiKey;
      onSubmit(data);
    }
  };

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-cyan-400" />
            ) : (
              <Search className="w-5 h-5 text-cyan-400" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {isEditing ? 'Edit Search Backend' : 'Add Search Backend'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditing ? 'Update backend configuration' : 'Configure a web search provider'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="Tavily (Primary)"
            />
          </div>

          {/* Type (only on create; tavily is the only supported provider for v1) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">Type</label>
              <select
                value={type}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm cursor-pointer"
                disabled
              >
                <option value="tavily">Tavily</option>
              </select>
              <p className="text-xs text-muted-foreground mt-2">
                Additional providers (Brave, Exa, Serper) can be added later — each requires a
                new adapter in <code className="font-mono">worker/src/search.ts</code>.
              </p>
            </div>
          )}

          {/* Base URL */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Base URL</label>
            <input
              type="url"
              required
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              placeholder="https://api.tavily.com"
            />
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              API Key {isEditing && existingBackend?.hasApiKey && (
                <span className="text-amber-400">(leave blank to keep current)</span>
              )}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              placeholder={isEditing && existingBackend?.hasApiKey ? '••••••••' : 'tvly-...'}
            />
          </div>

          {/* Tavily tunables */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium">Search Settings</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Depth</label>
                <select
                  value={searchDepth}
                  onChange={(e) => setSearchDepth(e.target.value as 'basic' | 'advanced')}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm cursor-pointer"
                >
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Default Max Results</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={maxResultsDefault}
                  onChange={(e) => setMaxResultsDefault(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="5"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Cost / query (USD)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={costPerQueryUsd}
                  onChange={(e) => setCostPerQueryUsd(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="0.005"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Depth "advanced" is slower but returns higher-quality extracted content. Cost is
              informational only (used for admin reporting, not billing).
            </p>
          </div>

          {/* Operational advanced */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Advanced</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Priority</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Timeout (ms)</label>
                <input
                  type="number"
                  value={timeoutMs}
                  onChange={(e) => setTimeoutMs(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="30000"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Max Concurrent</label>
                <input
                  type="number"
                  value={maxConcurrent}
                  onChange={(e) => setMaxConcurrent(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="10"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Lower priority = higher preference when multiple backends are active.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-glow-violet px-5 py-2.5 rounded-xl text-sm"
            >
              <span>{isEditing ? 'Save Changes' : 'Add Backend'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
